import { useCallback, useEffect, useRef, useState } from 'react'
import { ButtonToolbar, Card, Col, Dropdown, Row } from 'react-bootstrap'
import { useHistory, useParams } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Filters, ActiveFilterViewer } from '@rankmi/rankmi-table'

import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import { Actions as ActionsExternalCourses } from 'store/actions/externalCourses.action'
import { Actions } from 'store/actions/externalCourseParticipant.action'
import * as externalCourseCertificate from 'store/actions/externalCourseCertificate.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import { IMainState } from 'store/states'
import { ExternalCourseParticipantsListParams } from 'api/administration/externalCourseParticipants/GetList'
import destroyCertificate from 'api/administration/externalCourseCertificates/Destroy'
import { ExternalCourseModelAPI } from 'models/externalCourse.model'
import { createRouteExternalCourse, PathRoutes } from 'enum/routes'
import { generateFilterViewerLocale } from 'helpers/translations.helper'
import { useAccess } from 'contexts/AccessContext'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import { CourseData } from 'components/ExternalCourses/CourseData'
import { UploadCertificates, Table } from './components'
import { usePrefixTranslation, useFiltersColumns } from './hooks'
import { DeleteMany } from './components/DeleteMany'
import { DeleteManyCertificates } from './components/DeleteManyCertificates'
import styles from './ExternalCourse.module.scss'

export const ExternalCourse = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { loading, data } = useSelector(
    ({ externalCourses }: IMainState) => externalCourses.externalCourse
  )
  const { id } = useParams<Record<'id', string>>()
  const history = useHistory()

  useEffect(() => {
    dispatch(ActionsExternalCourses.getAdminExternalCourse({ id }))
  }, [dispatch, id])

  let bounceId: any
  const { total, list } = useSelector(
    ({ externalCourseParticipants }: IMainState) => externalCourseParticipants
  )
  const { can, loading: accessLoading, access } = useAccess()
  const canManageExternal = can('manageExternalCourses')
  const canDownloadReport = access.canDownloadReportWithViewAccess()
  const [searchText, setSearchText] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [page, setPage] = useState(1)
  const [deleteMany, setDeleteMany] = useState(false)
  const [deleteManyCertificates, setDeleteManyCertificates] = useState(false)
  const [sort, setSort] = useState({})
  const [perPage, setPerPage] = useState(10)
  const [selectedRows, setSelectedRows] = useState<
    ExternalCourseModelAPI['id'][]
  >([])
  const [fastFilters, setFastFilters] = useState(0)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const tRelative = usePrefixTranslation()
  const searchTextRef = useRef<any>()
  const filtersColumns = useFiltersColumns()
  const [translations, setTranslations] = useState<any>()
  useEffect(() => {
    setTranslations(() => generateFilterViewerLocale())
  }, [])
  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  const getList = useCallback(
    () =>
      dispatch(
        Actions.getList({
          externalCourseId: id,
          params: {
            page,
            searchText,
            sort,
            usersFilter: selectedFilters,
            fastFilters,
            perPage,
          },
        })
      ),
    [
      dispatch,
      id,
      page,
      searchText,
      sort,
      selectedFilters,
      fastFilters,
      perPage,
    ]
  )

  useEffect(() => {
    searchTextRef.current = searchText
    const timer = setTimeout(() => {
      getList()
    }, 1000)
    return () => clearTimeout(timer)
  }, [getList, searchText])

  useEffect(() => {
    if (!accessLoading && !canManageExternal) {
      history.push(PathRoutes.MAIN)
    }
  }, [history, accessLoading, canManageExternal])

  const handleSearch = (value: string) => {
    if (bounceId) {
      clearTimeout(bounceId)
    }
    bounceId = setTimeout(() => {
      setSearchText(value.trim())
    }, 1000)
  }

  const getParams: ExternalCourseParticipantsListParams = {
    page,
    searchText,
    sort,
    fastFilters,
    perPage,
  }

  const destroyParticipants = () => {
    dispatch(
      Actions.destroyParticipants({
        ids: selectedRows,
        params: getParams,
        externalCourseId: id,
        callback: () => {
          setSelectedRows([])
        },
      })
    )
    setDeleteMany(false)
  }

  const destroyCertificates = () => {
    const certificates = list
      .filter(({ id }) => selectedRows.includes(id))
      .map(({ certificate }) => certificate)
    const certificateIds: (string | number)[] = []
    for (const certificate of certificates)
      if (certificate) certificateIds.push(certificate.id)
    dispatch(
      externalCourseCertificate.Actions.destroyCertificates({
        ids: certificateIds,
        params: getParams,
        externalCourseId: id,
        callback: () => {
          setSelectedRows([])
        },
      })
    )
    setDeleteManyCertificates(false)
  }

  const onDestroyCertificate = async (id: string | number) => {
    await destroyCertificate(id)
    getList()
  }

  const handleDelete = (participant: number) => {
    dispatch(
      Actions.destroyParticipant({
        id: participant,
        externalCourseId: id,
        params: getParams,
      })
    )
  }

  const handleDownload = (id: number) => {
    dispatch(
      ActionsExternalCourses.downloadExternalCourses({
        id,
      })
    )
  }

  return (
    <>
      <div className={`${styles.cardContainer}`}>
        <Breadcrumb
          separator={
            <FontAwesomeIcon
              className={styles.breadcrumbSeparator}
              icon={faChevronRight}
            />
          }
        >
          <BreadcrumbItem
            href={PathRoutes.EXTERNAL_COURSES}
            text={t('administration.externalCourses.name')}
          />
          <BreadcrumbItem
            href={createRouteExternalCourse({ id })}
            text={
              data?.name || (
                <Loader
                  classes={{ loader: 'p-0' }}
                  sizes={LoaderSizes.ExtraSmall}
                  color
                />
              )
            }
            active
          />
        </Breadcrumb>
        {loading && <Loader sizes={LoaderSizes.ExtraSmall} color />}
        {!loading && !!data && (
          <>
            <CourseData data={data} />
            <Row className="mt-2">
              <Col>
                <ButtonToolbar className="float-left">
                  <Filters
                    columns={filtersColumns}
                    onChange={(data: any) => {
                      setSelectedFilters(data)
                    }}
                    open={showFiltersModal}
                    onVisibilityChange={(state: boolean) =>
                      setShowFiltersModal(state)
                    }
                    locale={translations}
                  />
                </ButtonToolbar>
                <ButtonToolbar className={`float-right ${styles.buttons}`}>
                  {canManageExternal && (
                    <Dropdown
                      id="dropdown-manage"
                      className={styles.dropdownManage}
                      alignRight
                    >
                      <Dropdown.Toggle
                        id="dropdown-manage-toggle"
                        bsPrefix="noCaret"
                        variant={'secondary'}
                      >
                        {tRelative('list.dropdown.actions')}{' '}
                        <FontAwesomeIcon icon={faChevronDown} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <UploadCertificates
                          externalCourse={data}
                          participantList={{
                            externalCourseId: id,
                            params: getParams,
                          }}
                        />
                        <Dropdown.Item
                          disabled={!allSelected && !selectedRows.length}
                          onClick={() => setDeleteMany(true)}
                        >
                          {tRelative('item.dropdown.removeParticipants', {
                            count: !allSelected
                              ? selectedRows.length
                              : total - selectedRows.length,
                          })}
                        </Dropdown.Item>
                        <Dropdown.Item
                          disabled={!allSelected && !selectedRows.length}
                          onClick={() => setDeleteManyCertificates(true)}
                        >
                          {tRelative('item.dropdown.removeCertificates', {
                            count: !allSelected
                              ? selectedRows.length
                              : total - selectedRows.length,
                          })}
                        </Dropdown.Item>
                        {canDownloadReport && (
                          <Dropdown.Item
                            onClick={() => handleDownload(parseInt(id))}
                          >
                            {tRelative('item.dropdown.downloadDetailedReport')}
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </ButtonToolbar>
              </Col>
            </Row>
            <Row>
              <Col>
                <ActiveFilterViewer
                  activeFilters={selectedFilters}
                  onChange={(data: any) => setSelectedFilters(data)}
                  openFilters={(state: boolean) => setShowFiltersModal(state)}
                  columns={filtersColumns}
                  locale={translations}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Table
                  onDelete={handleDelete}
                  onDeleteCertificate={onDestroyCertificate}
                  setSearch={handleSearch}
                  setPage={setPage}
                  setSort={setSort}
                  setPerPage={setPerPage}
                  setSelectedRows={setSelectedRows}
                  selectedRows={selectedRows}
                  setFastFilters={setFastFilters}
                  setAllSelected={setAllSelected}
                />
              </Col>
            </Row>
          </>
        )}
      </div>
      <DeleteMany
        show={deleteMany}
        count={selectedRows.length}
        onClose={() => {
          setDeleteMany(false)
        }}
        onAccept={() => {
          destroyParticipants()
        }}
      />
      <DeleteManyCertificates
        show={deleteManyCertificates}
        count={selectedRows.length}
        onClose={() => {
          setDeleteManyCertificates(false)
        }}
        onAccept={() => {
          destroyCertificates()
        }}
      />
    </>
  )
}

export default ExternalCourse
